export type InstanceExecutiveAlertSeverity = 'CRITICAL' | 'ATTENTION' | 'INFORMATION';

export type InstanceExecutiveAlert = {
  code: string;
  label: string;
  detail: string;
  severity: InstanceExecutiveAlertSeverity;
  affectsScore: boolean;
  periodLabel: string;
  triggerData: string;
};

export type InstanceExecutiveAlertInput = {
  readError?: string | null;
  closedKpiDetail?: string | null;
  pendingKpis: number;
  selectedMonthLabel: string;
  selectedYear?: number;
  planHasClosedObligation: boolean;
  planProgress: number;
  sectionsWithGoalAndZero: number;
  narrativeMissing: boolean;
  noConfiguration: boolean;
  noObligation: boolean;
  noPlan: boolean;
};

const SEVERITY_PRIORITY: Record<InstanceExecutiveAlertSeverity, number> = {
  CRITICAL: 3,
  ATTENTION: 2,
  INFORMATION: 1
};

export const INSTANCE_EXECUTIVE_ALERT_SEVERITY_LABELS: Record<InstanceExecutiveAlertSeverity, string> = {
  CRITICAL: 'Crítica',
  ATTENTION: 'Atención',
  INFORMATION: 'Informativa'
};

const capitalize = (value: string): string => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

const makeAlert = (
  input: InstanceExecutiveAlertInput,
  alert: Omit<InstanceExecutiveAlert, 'periodLabel'>
): InstanceExecutiveAlert => ({
  ...alert,
  periodLabel: `${capitalize(input.selectedMonthLabel)} ${input.selectedYear ?? 2026}`
});

export const buildInstanceExecutiveAlerts = (input: InstanceExecutiveAlertInput): InstanceExecutiveAlert[] => {
  const alerts: InstanceExecutiveAlert[] = [];

  if (input.readError) alerts.push(makeAlert(input, { code: 'READ_ERROR', label: 'Error de lectura', detail: input.readError, severity: 'CRITICAL', affectsScore: false, triggerData: 'La lectura remota no estuvo disponible.' }));
  if (input.closedKpiDetail) alerts.push(makeAlert(input, { code: 'EXPIRED_INDICATOR', label: 'Indicador vencido', detail: input.closedKpiDetail, severity: 'CRITICAL', affectsScore: true, triggerData: input.closedKpiDetail }));
  if (input.pendingKpis > 0) alerts.push(makeAlert(input, {
    code: 'CAPTURE_PENDING', label: 'Captura pendiente',
    detail: input.pendingKpis === 1
      ? `Un indicador aún no tiene captura registrada para ${input.selectedMonthLabel}.`
      : `${input.pendingKpis} indicadores aún no tienen captura registrada para ${input.selectedMonthLabel}.`,
    severity: 'ATTENTION', affectsScore: false,
    triggerData: `${input.pendingKpis} indicador${input.pendingKpis === 1 ? '' : 'es'} sin captura en el periodo.`
  }));
  if (input.planHasClosedObligation && input.planProgress <= 0) alerts.push(makeAlert(input, {
    code: 'PLAN_WITHOUT_PROGRESS', label: 'Plan en cero',
    detail: 'El plan tiene obligación vencida y no registra avance acumulado.', severity: 'CRITICAL', affectsScore: true,
    triggerData: `Avance acumulado registrado: ${input.planProgress}.`
  }));
  if (input.sectionsWithGoalAndZero > 0) alerts.push(makeAlert(input, {
    code: 'PLAN_SECTIONS_WITHOUT_PROGRESS', label: 'Plan en cero',
    detail: `${input.sectionsWithGoalAndZero} sección(es) con meta mayor que cero y avance acumulado en cero.`,
    severity: 'ATTENTION', affectsScore: true,
    triggerData: `${input.sectionsWithGoalAndZero} sección${input.sectionsWithGoalAndZero === 1 ? '' : 'es'} con meta y avance cero.`
  }));
  if (input.narrativeMissing) alerts.push(makeAlert(input, {
    code: 'ANALYSIS_PENDING', label: 'Análisis pendiente', detail: 'Falta registrar la explicación mensual de resultados.',
    severity: 'ATTENTION', affectsScore: false,
    triggerData: 'Resumen y observaciones sin captura.'
  }));
  if (input.noConfiguration) alerts.push(makeAlert(input, {
    code: 'NO_CONFIGURATION', label: 'Sin configurar',
    detail: 'La instancia aún no cuenta con plan ni indicadores configurados.', severity: 'INFORMATION', affectsScore: false,
    triggerData: 'Plan aplicable: no; indicadores configurados: 0.'
  }));
  else if (input.noPlan) alerts.push(makeAlert(input, {
    code: 'NO_PLAN', label: 'Sin plan', detail: 'No existe un plan con meta aplicable para el periodo.',
    severity: 'INFORMATION', affectsScore: false,
    triggerData: 'Meta de plan aplicable: 0.'
  }));
  if (input.noObligation) alerts.push(makeAlert(input, {
    code: 'NO_EXPIRED_OBLIGATION', label: 'Sin obligación',
    detail: 'El plan no tiene una meta vencida en periodos cerrados.', severity: 'INFORMATION', affectsScore: false,
    triggerData: 'Periodos cerrados con meta vencida: 0.'
  }));

  return alerts.sort((a, b) => SEVERITY_PRIORITY[b.severity] - SEVERITY_PRIORITY[a.severity]);
};

export const limitInstanceExecutiveAlerts = (alerts: InstanceExecutiveAlert[], limit = 3) => ({
  visible: alerts.slice(0, Math.max(0, limit)),
  hidden: alerts.slice(Math.max(0, limit)),
  additionalCount: Math.max(0, alerts.length - Math.max(0, limit))
});

export const formatHiddenInstanceAlertSummary = (alerts: InstanceExecutiveAlert[]): string => {
  const total = alerts.length;
  if (total === 0) return '';
  const parts = [`+${total} ${total === 1 ? 'alerta' : 'alertas'}`];
  const counts = alerts.reduce<Record<InstanceExecutiveAlertSeverity, number>>((acc, alert) => {
    acc[alert.severity] += 1;
    return acc;
  }, { CRITICAL: 0, ATTENTION: 0, INFORMATION: 0 });
  const labels: Record<InstanceExecutiveAlertSeverity, [string, string]> = {
    CRITICAL: ['crítica', 'críticas'],
    ATTENTION: ['atención', 'atención'],
    INFORMATION: ['informativa', 'informativas']
  };
  (['CRITICAL', 'ATTENTION', 'INFORMATION'] as const).forEach((severity) => {
    const count = counts[severity];
    if (count > 0) parts.push(`${count} ${labels[severity][count === 1 ? 0 : 1]}`);
  });
  return parts.join(' · ');
};
